import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import GuestOverlay from "@/components/auth/GuestOverlay";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { submitKieTask, getKieApiKey, getKieCallbackUrl } from "@/lib/kie-service";
import { Progress } from "@/components/ui/progress";
import {
    Mic,
    Sparkles,
    Loader2,
    Download,
    Coins,
    History,
    Repeat,
    Trash2,
    RefreshCw,
    CheckCircle2,
    AlertTriangle,
    Play,
    Pause,
    Volume2,
    Globe,
    User,
    Users,
    Plus,
    X,
    MessageSquare
} from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { checkTaskStatus } from "@/lib/task-checker";

// Pricing: 14 credits per 1,000 characters
const CREDITS_PER_1000_CHARS = 14;
const MIN_CREDITS = 1;

const calculateCredits = (text: string): number => {
    const charCount = text.trim().length;
    if (charCount === 0) return 0;
    const credits = Math.ceil((charCount / 1000) * CREDITS_PER_1000_CHARS);
    return Math.max(credits, MIN_CREDITS);
};

const languageOptions = [
    { value: "auto", label: "Auto Detect" },
    { value: "th", label: "ไทย (Thai)" },
    { value: "en", label: "English" },
    { value: "zh", label: "中文 (Chinese)" },
    { value: "ja", label: "日本語 (Japanese)" },
    { value: "ko", label: "한국어 (Korean)" },
];

interface VoiceOption {
    id: string;
    name: string;
    description: string;
    category: string;
    gender: "male" | "female";
}

// ElevenLabs V3 voices
const voiceOptions: VoiceOption[] = [
    // Female
    { id: "cgSgspJ2msm6clMCkdW9", name: "Jessica", description: "Playful, Bright, Warm", category: "Conversational", gender: "female" },
    { id: "Xb7hH8MSUJpSbSDYk0k2", name: "Ellen", description: "Serious, Direct, Confident", category: "Conversational", gender: "female" },
    { id: "XrExE9yKIg1WjnnlVkGX", name: "Juniper", description: "Grounded, Professional", category: "Conversational", gender: "female" },
    { id: "pFZP5JQG7iQjIQuC4Bku", name: "Jane", description: "Professional Audiobook Reader", category: "Narration", gender: "female" },
    { id: "FGY2WhTYpPnrIDTdsKH5", name: "Arabella", description: "Mysterious, Emotive", category: "Narration", gender: "female" },
    { id: "EXAVITQu4vr4xnSDxMaL", name: "Sarah", description: "Soft, Young, Friendly", category: "Conversational", gender: "female" },
    // Male
    { id: "TX3LPaxmHKxFdv7VOQHJ", name: "Liam", description: "Energetic, Bold, Dynamic", category: "Conversational", gender: "male" },
    { id: "IKne3meq5aSn9XLyUdCD", name: "James", description: "Husky, Engaging, Bold", category: "Narration", gender: "male" },
    { id: "nPczCjzI2devNBz1zQrb", name: "Brian", description: "Deep, Authoritative, British", category: "News", gender: "male" },
    { id: "JBFqnCBsd6RMkjVDRZzb", name: "George", description: "Warm, Mature, Articulate", category: "Narration", gender: "male" },
    { id: "onwK4e9ZLuTAKqWW03F9", name: "Daniel", description: "British, Confident, Clear", category: "News", gender: "male" },
    { id: "iP95p4xoKVk53GoZ742B", name: "Chris", description: "Casual, Friendly, American", category: "Conversational", gender: "male" },
];

interface DialogueLine {
    id: string;
    speaker: 1 | 2;
    text: string;
}

interface SpeechLog {
    id: string;
    prompt: string;
    result_url: string | null;
    status: string;
    created_at: string;
    model: string;
    credits_used?: number;
}

const TextToDialogue = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { user, loading: authLoading, profile, refreshProfile } = useAuth();
    const { toast } = useToast();

    // Speakers
    const [speaker1Voice, setSpeaker1Voice] = useState(voiceOptions[0].id); // Jessica
    const [speaker2Voice, setSpeaker2Voice] = useState(voiceOptions[6].id); // Liam
    const [speaker1Name, setSpeaker1Name] = useState("A");
    const [speaker2Name, setSpeaker2Name] = useState("B");

    // Dialogue lines
    const [dialogueLines, setDialogueLines] = useState<DialogueLine[]>([
        { id: "1", speaker: 1, text: "" },
        { id: "2", speaker: 2, text: "" },
    ]);

    const [stability, setStability] = useState(0.5);
    const [languageCode, setLanguageCode] = useState("auto");
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState("");

    // Credits calculation
    const [calculatedCredits, setCalculatedCredits] = useState(0);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Recent speeches
    const [recentSpeeches, setRecentSpeeches] = useState<SpeechLog[]>([]);
    const [loadingSpeeches, setLoadingSpeeches] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState<string | null>(null);
    const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
    const [audioElements, setAudioElements] = useState<{ [key: string]: HTMLAudioElement }>({});

    const userCredits = profile?.credits || 0;
    const hasEnoughCredits = authLoading || calculatedCredits === 0 || userCredits >= calculatedCredits;

    // Calculate total text for credits
    const getTotalText = useCallback(() => {
        return dialogueLines.map(line => line.text).join(" ");
    }, [dialogueLines]);

    // Debounced credits calculation
    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const credits = calculateCredits(getTotalText());
            setCalculatedCredits(credits);
        }, 300);

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [dialogueLines, getTotalText]);

    // Add dialogue line
    const addDialogueLine = (speaker: 1 | 2) => {
        setDialogueLines([
            ...dialogueLines,
            { id: Date.now().toString(), speaker, text: "" }
        ]);
    };

    // Update dialogue line
    const updateDialogueLine = (id: string, text: string) => {
        setDialogueLines(lines =>
            lines.map(line => line.id === id ? { ...line, text } : line)
        );
    };

    // Remove dialogue line
    const removeDialogueLine = (id: string) => {
        if (dialogueLines.length <= 2) return; // Keep at least 2 lines
        setDialogueLines(lines => lines.filter(line => line.id !== id));
    };

    // Fetch recent speeches
    const fetchRecentSpeeches = async () => {
        if (!user) return;
        setLoadingSpeeches(true);
        try {
            const { data, error } = await supabase
                .from("generation_logs")
                .select("id, prompt, result_url, status, created_at, model, credits_used")
                .eq("user_id", user.id)
                .eq("service_type", "speech" as any)
                .eq("model", "elevenlabs/text-to-dialogue-v3")
                .order("created_at", { ascending: false })
                .limit(10);

            if (error) throw error;
            setRecentSpeeches((data || []) as SpeechLog[]);
        } catch (err) {
            console.error("Error fetching recent speeches:", err);
            setRecentSpeeches([]);
        } finally {
            setLoadingSpeeches(false);
        }
    };

    useEffect(() => {
        if (user) {
            fetchRecentSpeeches();
        }
    }, [user]);

    const handleGenerate = async () => {
        const validLines = dialogueLines.filter(line => line.text.trim());
        if (validLines.length < 2) {
            toast({
                title: t("กรุณากรอกบทสนทนาอย่างน้อย 2 บรรทัด", "Please enter at least 2 dialogue lines"),
                variant: "destructive",
            });
            return;
        }

        if (!user || !profile) {
            toast({
                title: t("กรุณาเข้าสู่ระบบ", "Please log in"),
                variant: "destructive",
            });
            return;
        }

        const finalCreditsRequired = calculateCredits(getTotalText());

        if (finalCreditsRequired === 0) {
            toast({
                title: t("กรุณากรอกข้อความ", "Please enter text"),
                variant: "destructive",
            });
            return;
        }

        if (userCredits < finalCreditsRequired) {
            toast({
                title: t("เครดิตไม่เพียงพอ", "Insufficient credits"),
                description: t(
                    `ต้องการ ${finalCreditsRequired} เครดิต คุณมี ${userCredits} เครดิต`,
                    `Need ${finalCreditsRequired} credits, you have ${userCredits}`
                ),
                variant: "destructive",
            });
            return;
        }

        setIsGenerating(true);
        setGenerationProgress(10);
        setStatusMessage(t("กำลังเตรียมการ...", "Preparing..."));

        try {
            // Build dialogue array for API
            const dialogueArray = validLines.map(line => ({
                text: line.text,
                voice: line.speaker === 1 ? speaker1Voice : speaker2Voice
            }));

            // Create prompt for logging
            const promptText = validLines.map(line =>
                `[${line.speaker === 1 ? speaker1Name : speaker2Name}]: ${line.text}`
            ).join("\n");

            // Deduct credits
            const { data: deductResult, error: deductError } = await supabase.rpc(
                "deduct_credits_for_generation" as any,
                {
                    p_user_id: user.id,
                    p_credits_required: finalCreditsRequired,
                    p_service_type: "speech",
                    p_model: "elevenlabs/text-to-dialogue-v3",
                    p_prompt: promptText.substring(0, 500),
                }
            );

            if (deductError || !deductResult?.success) {
                throw new Error(deductResult?.error || "Failed to deduct credits");
            }

            const logId = deductResult.log_id;
            setGenerationProgress(20);
            setStatusMessage(t("กำลังส่งคำขอ...", "Sending request..."));

            // Get API key and callback URL
            const apiKey = await getKieApiKey();
            const callbackUrl = await getKieCallbackUrl();

            if (!apiKey) {
                throw new Error("API key not configured");
            }

            // Submit task to Kie API with dialogue format
            // Note: stability is at root level per API spec, language_code is optional (auto-detect)
            const taskBody = {
                model: "elevenlabs/text-to-dialogue-v3",
                input: {
                    dialogue: dialogueArray
                },
                stability: stability,
                callBackUrl: callbackUrl ? `${callbackUrl}?logId=${logId}` : undefined,
            };

            const response = await submitKieTask("/api/v1/jobs/createTask", apiKey, taskBody);

            if (response.code !== 200 || !response.data?.taskId) {
                throw new Error(response.msg || "Failed to create task");
            }

            const taskId = response.data.taskId;
            setGenerationProgress(40);
            setStatusMessage(t("กำลังสร้างเสียง...", "Generating dialogue..."));

            // Update log with task ID
            await supabase
                .from("generation_logs")
                .update({ status: "processing", task_id: taskId })
                .eq("id", logId);

            await refreshProfile();

            toast({
                title: t("กำลังสร้างบทสนทนา...", "Generating dialogue..."),
                description: t(
                    "คุณสามารถดูสถานะได้ที่ประวัติด้านล่าง",
                    "You can check the status in the history below"
                ),
            });

            fetchRecentSpeeches();
        } catch (error: any) {
            console.error("Generation error:", error);
            toast({
                title: t("เกิดข้อผิดพลาด", "Error"),
                description: error.message,
                variant: "destructive",
            });
        } finally {
            setIsGenerating(false);
            setGenerationProgress(0);
            setStatusMessage("");
        }
    };

    const handleCheckStatus = async (logId: string) => {
        setCheckingStatus(logId);
        try {
            const result = await checkTaskStatus(logId);
            if (result.success && result.status === "completed") {
                toast({
                    title: t("เสร็จแล้ว!", "Completed!"),
                    description: t("เสียงพร้อมใช้งานแล้ว", "Audio is ready"),
                });
                fetchRecentSpeeches();
            } else if (result.status === "processing") {
                toast({
                    title: t("กำลังประมวลผล", "Processing"),
                    description: t("กรุณารอสักครู่", "Please wait"),
                });
            } else if (result.status === "failed") {
                toast({
                    title: t("ล้มเหลว", "Failed"),
                    description: result.message || t("เกิดข้อผิดพลาด", "An error occurred"),
                    variant: "destructive",
                });
                fetchRecentSpeeches();
            }
        } catch (error: any) {
            toast({
                title: t("เกิดข้อผิดพลาด", "Error"),
                description: error.message,
                variant: "destructive",
            });
        } finally {
            setCheckingStatus(null);
        }
    };

    const handleDelete = async (id: string) => {
        try {
            const { error } = await supabase.from("generation_logs").delete().eq("id", id);
            if (error) throw error;
            toast({ title: t("ลบสำเร็จ", "Deleted successfully") });
            fetchRecentSpeeches();
        } catch (error: any) {
            toast({
                title: t("เกิดข้อผิดพลาด", "Error"),
                description: error.message,
                variant: "destructive",
            });
        }
    };

    const handleDownload = async (url: string, filename: string) => {
        try {
            const response = await fetch(url, { mode: "cors" });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch {
            window.open(url, "_blank");
        }
    };

    const getAudioUrl = (log: SpeechLog): string | null => {
        if (!log.result_url) return null;
        let url = log.result_url;
        if (url.startsWith("{")) {
            try {
                const parsed = JSON.parse(url);
                url = parsed.resultUrls?.[0] || parsed.url || parsed.audio_url || url;
            } catch { }
        }
        return url.startsWith("http") ? url : null;
    };

    const togglePlayAudio = (logId: string, audioUrl: string) => {
        if (playingAudioId === logId) {
            audioElements[logId]?.pause();
            setPlayingAudioId(null);
        } else {
            if (playingAudioId && audioElements[playingAudioId]) {
                audioElements[playingAudioId].pause();
            }
            let audio = audioElements[logId];
            if (!audio) {
                audio = new Audio(audioUrl);
                audio.onended = () => setPlayingAudioId(null);
                setAudioElements(prev => ({ ...prev, [logId]: audio }));
            }
            audio.play();
            setPlayingAudioId(logId);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat("th-TH", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        }).format(date);
    };

    const renderStatusBadge = (status: string) => {
        switch (status) {
            case "completed":
                return <Badge className="bg-green-500">{t("สำเร็จ", "Completed")}</Badge>;
            case "processing":
                return <Badge className="bg-blue-500 animate-pulse">{t("กำลังประมวลผล", "Processing")}</Badge>;
            case "failed":
                return <Badge variant="destructive">{t("ล้มเหลว", "Failed")}</Badge>;
            default:
                return <Badge variant="outline">{t("รอดำเนินการ", "Pending")}</Badge>;
        }
    };

    const getVoiceName = (voiceId: string) => {
        return voiceOptions.find(v => v.id === voiceId)?.name || "Unknown";
    };

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            <Navbar />
            {!user && <GuestOverlay />}

            <main className="pt-24 pb-16 container mx-auto px-4 max-w-6xl">
                {/* Header */}
                <div className="flex flex-col items-center mb-10 text-center">
                    <Badge variant="outline" className="mb-4 py-1 px-4 text-primary border-primary/20 bg-primary/5">
                        <Users className="h-3.5 w-3.5 mr-2" />
                        ElevenLabs Text-to-Dialogue V3
                    </Badge>
                    <h1 className="text-4xl md:text-5xl font-bold font-heading mb-4 tracking-tight">
                        {t("บทสนทนา 2 คน", "Two-Person Dialogue")} <span className="text-gradient">AI</span>
                    </h1>
                    <p className="text-muted-foreground max-w-2xl text-lg">
                        {t(
                            "สร้างบทสนทนาระหว่าง 2 คน พร้อมอารมณ์และจังหวะการพูดที่เป็นธรรมชาติ",
                            "Create natural conversations between 2 speakers with emotions and realistic timing"
                        )}
                    </p>
                </div>

                <div className="grid lg:grid-cols-12 gap-8 items-start">
                    {/* Main Form */}
                    <div className="lg:col-span-8">
                        <Card className="border-border/50 bg-card/50 backdrop-blur-md shadow-xl">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <MessageSquare className="h-5 w-5 text-primary" />
                                    {t("สร้างบทสนทนา", "Create Dialogue")}
                                </CardTitle>
                                <CardDescription>
                                    {t(
                                        "เลือกเสียงสำหรับแต่ละคน และพิมพ์บทสนทนา รองรับอารมณ์ เช่น [excitedly] [whispering]",
                                        "Select voices for each speaker and type the dialogue. Supports emotions like [excitedly] [whispering]"
                                    )}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Speaker Selection */}
                                <div className="grid md:grid-cols-2 gap-4">
                                    {/* Speaker 1 */}
                                    <div className="p-4 rounded-xl border border-pink-500/20 bg-pink-500/5">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center">
                                                <User className="h-4 w-4 text-pink-500" />
                                            </div>
                                            <Input
                                                value={speaker1Name}
                                                onChange={(e) => setSpeaker1Name(e.target.value)}
                                                className="h-8 w-20 text-sm font-medium"
                                                placeholder="ชื่อ"
                                            />
                                        </div>
                                        <Select value={speaker1Voice} onValueChange={setSpeaker1Voice}>
                                            <SelectTrigger className="bg-background/50">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {voiceOptions.map(voice => (
                                                    <SelectItem key={voice.id} value={voice.id}>
                                                        <div className="flex items-center gap-2">
                                                            <span className={voice.gender === "female" ? "text-pink-500" : "text-blue-500"}>
                                                                {voice.gender === "female" ? "♀" : "♂"}
                                                            </span>
                                                            <span>{voice.name}</span>
                                                            <span className="text-xs text-muted-foreground">- {voice.description}</span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Speaker 2 */}
                                    <div className="p-4 rounded-xl border border-blue-500/20 bg-blue-500/5">
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                                <User className="h-4 w-4 text-blue-500" />
                                            </div>
                                            <Input
                                                value={speaker2Name}
                                                onChange={(e) => setSpeaker2Name(e.target.value)}
                                                className="h-8 w-20 text-sm font-medium"
                                                placeholder="ชื่อ"
                                            />
                                        </div>
                                        <Select value={speaker2Voice} onValueChange={setSpeaker2Voice}>
                                            <SelectTrigger className="bg-background/50">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {voiceOptions.map(voice => (
                                                    <SelectItem key={voice.id} value={voice.id}>
                                                        <div className="flex items-center gap-2">
                                                            <span className={voice.gender === "female" ? "text-pink-500" : "text-blue-500"}>
                                                                {voice.gender === "female" ? "♀" : "♂"}
                                                            </span>
                                                            <span>{voice.name}</span>
                                                            <span className="text-xs text-muted-foreground">- {voice.description}</span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                {/* Dialogue Lines */}
                                <div className="space-y-3">
                                    <label className="text-sm font-medium flex items-center justify-between">
                                        <span>{t("บทสนทนา", "Dialogue Lines")}</span>
                                        <span className="text-xs text-muted-foreground">
                                            {t("เคล็ดลับ: ใช้ [excitedly] [whispering] [laughing] เพื่อเพิ่มอารมณ์", "Tip: Use [excitedly] [whispering] [laughing] for emotions")}
                                        </span>
                                    </label>

                                    {dialogueLines.map((line, index) => (
                                        <div key={line.id} className="flex items-start gap-2">
                                            {/* Speaker indicator */}
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                className={`shrink-0 w-16 ${line.speaker === 1
                                                        ? "border-pink-500/50 bg-pink-500/10 text-pink-500 hover:bg-pink-500/20"
                                                        : "border-blue-500/50 bg-blue-500/10 text-blue-500 hover:bg-blue-500/20"
                                                    }`}
                                                onClick={() => {
                                                    setDialogueLines(lines =>
                                                        lines.map(l => l.id === line.id ? { ...l, speaker: l.speaker === 1 ? 2 : 1 } : l)
                                                    );
                                                }}
                                            >
                                                {line.speaker === 1 ? speaker1Name : speaker2Name}
                                            </Button>

                                            {/* Text input */}
                                            <Input
                                                value={line.text}
                                                onChange={(e) => updateDialogueLine(line.id, e.target.value)}
                                                placeholder={t(
                                                    `ตัวอย่าง: ${line.speaker === 1 ? "[excitedly] สวัสดีครับ" : "สวัสดีค่ะ ยินดีที่ได้รู้จัก"}`,
                                                    `Example: ${line.speaker === 1 ? "[excitedly] Hello!" : "Hi! Nice to meet you!"}`
                                                )}
                                                className="flex-1"
                                            />

                                            {/* Remove button */}
                                            {dialogueLines.length > 2 && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    className="shrink-0 text-muted-foreground hover:text-destructive"
                                                    onClick={() => removeDialogueLine(line.id)}
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}

                                    {/* Add line buttons */}
                                    <div className="flex gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="flex-1 border-pink-500/30 text-pink-500 hover:bg-pink-500/10"
                                            onClick={() => addDialogueLine(1)}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            {t("เพิ่มบรรทัด", "Add Line")} {speaker1Name}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="flex-1 border-blue-500/30 text-blue-500 hover:bg-blue-500/10"
                                            onClick={() => addDialogueLine(2)}
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            {t("เพิ่มบรรทัด", "Add Line")} {speaker2Name}
                                        </Button>
                                    </div>
                                </div>

                                {/* Settings */}
                                <div className="grid md:grid-cols-2 gap-6">
                                    {/* Language */}
                                    <div className="space-y-3">
                                        <label className="text-sm font-medium flex items-center gap-2">
                                            <Globe className="h-4 w-4 text-primary" />
                                            {t("ภาษา", "Language")}
                                        </label>
                                        <Select value={languageCode} onValueChange={setLanguageCode}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {languageOptions.map(lang => (
                                                    <SelectItem key={lang.value} value={lang.value}>
                                                        {lang.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Stability */}
                                    <div className="space-y-3">
                                        <label className="text-sm font-medium flex items-center justify-between">
                                            <span>{t("ความเสถียรของเสียง", "Voice Stability")}</span>
                                            <span className="text-primary font-mono">{stability.toFixed(1)}</span>
                                        </label>
                                        <Slider
                                            value={[stability]}
                                            onValueChange={(val) => setStability(val[0])}
                                            min={0}
                                            max={1}
                                            step={0.1}
                                            className="py-2"
                                        />
                                    </div>
                                </div>

                                {/* Progress */}
                                {isGenerating && (
                                    <div className="space-y-2">
                                        <Progress value={generationProgress} className="h-2" />
                                        <p className="text-sm text-center text-muted-foreground">{statusMessage}</p>
                                    </div>
                                )}

                                {/* Generate Button */}
                                <Button
                                    size="lg"
                                    className="w-full h-14 text-lg font-bold shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300"
                                    onClick={handleGenerate}
                                    disabled={isGenerating || !hasEnoughCredits || calculatedCredits === 0}
                                >
                                    {isGenerating ? (
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                    ) : (
                                        <Sparkles className="mr-2 h-5 w-5" />
                                    )}
                                    {t("สร้างบทสนทนา", "Generate Dialogue")}
                                    <span className="ml-2 opacity-70 text-sm font-normal">
                                        ({calculatedCredits > 0 ? calculatedCredits : "-"} {t("เครดิต", "Credits")})
                                    </span>
                                </Button>

                                {!hasEnoughCredits && calculatedCredits > 0 && (
                                    <p className="text-center text-sm text-destructive">
                                        {t(
                                            `เครดิตไม่เพียงพอ (ต้องการ ${calculatedCredits} เครดิต)`,
                                            `Insufficient credits (need ${calculatedCredits} credits)`
                                        )}
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-4 space-y-6">
                        {/* Credits Card */}
                        <Card className="border-primary/20 bg-primary/5">
                            <CardContent className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Coins className="h-5 w-5 text-primary" />
                                        <span className="font-medium">{t("เครดิตของคุณ", "Your Credits")}</span>
                                    </div>
                                    <span className="text-2xl font-bold text-primary">
                                        {userCredits.toLocaleString()}
                                    </span>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Info Card */}
                        <Card className="border-border/50 bg-card/50">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <MessageSquare className="h-4 w-4 text-primary" />
                                    {t("เคล็ดลับการใช้งาน", "Tips")}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 text-sm text-muted-foreground">
                                <p>• {t("คลิกที่ชื่อเพื่อสลับผู้พูด", "Click the name to switch speaker")}</p>
                                <p>• {t("ใช้ [excitedly] สำหรับความตื่นเต้น", "Use [excitedly] for excitement")}</p>
                                <p>• {t("ใช้ [whispering] สำหรับกระซิบ", "Use [whispering] for whispering")}</p>
                                <p>• {t("ใช้ [laughing] สำหรับหัวเราะ", "Use [laughing] for laughing")}</p>
                                <p>• {t("ใช้ ... สำหรับหยุดคิด", "Use ... for pausing")}</p>
                            </CardContent>
                        </Card>

                        {/* History */}
                        <Card className="border-border/50 bg-card/50">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <History className="h-4 w-4 text-primary" />
                                        {t("ประวัติล่าสุด", "Recent History")}
                                    </span>
                                    <Button variant="ghost" size="icon" onClick={fetchRecentSpeeches} disabled={loadingSpeeches}>
                                        <RefreshCw className={`h-4 w-4 ${loadingSpeeches ? "animate-spin" : ""}`} />
                                    </Button>
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loadingSpeeches ? (
                                    <div className="flex justify-center py-4">
                                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                    </div>
                                ) : recentSpeeches.length === 0 ? (
                                    <p className="text-center text-sm text-muted-foreground py-4">
                                        {t("ยังไม่มีประวัติ", "No history yet")}
                                    </p>
                                ) : (
                                    <div className="space-y-2">
                                        {recentSpeeches.map(log => {
                                            const audioUrl = getAudioUrl(log);
                                            const isPlaying = playingAudioId === log.id;

                                            return (
                                                <div
                                                    key={log.id}
                                                    className="p-3 rounded-lg bg-muted/30 border border-border/50"
                                                >
                                                    <div className="flex items-start justify-between gap-2 mb-2">
                                                        <p className="text-xs text-muted-foreground line-clamp-2 flex-1">
                                                            {log.prompt}
                                                        </p>
                                                        {renderStatusBadge(log.status)}
                                                    </div>
                                                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                                                        <span>{formatDate(log.created_at)}</span>
                                                        <div className="flex items-center gap-1">
                                                            {log.status === "processing" && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="h-7 w-7"
                                                                    onClick={() => handleCheckStatus(log.id)}
                                                                    disabled={checkingStatus === log.id}
                                                                >
                                                                    {checkingStatus === log.id ? (
                                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                                    ) : (
                                                                        <RefreshCw className="h-3.5 w-3.5" />
                                                                    )}
                                                                </Button>
                                                            )}
                                                            {audioUrl && (
                                                                <>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-7 w-7"
                                                                        onClick={() => togglePlayAudio(log.id, audioUrl)}
                                                                    >
                                                                        {isPlaying ? (
                                                                            <Pause className="h-3.5 w-3.5" />
                                                                        ) : (
                                                                            <Play className="h-3.5 w-3.5" />
                                                                        )}
                                                                    </Button>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-7 w-7"
                                                                        onClick={() => handleDownload(audioUrl, `dialogue_${log.id.slice(0, 8)}.mp3`)}
                                                                    >
                                                                        <Download className="h-3.5 w-3.5" />
                                                                    </Button>
                                                                </>
                                                            )}
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 text-destructive"
                                                                onClick={() => handleDelete(log.id)}
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default TextToDialogue;
